import { IconButton, PageModal } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

export type GalleryImage = {
  src: string;
  alt: string;
};

type Props = {
  images: GalleryImage[];
  title: string;
  isOpen: boolean;
  initialIndex: number;
  onClose: () => void;
};

export const PropertyImageGalleryModal = ({
  images,
  title,
  isOpen,
  initialIndex,
  onClose,
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const thumbnailReferences = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [initialIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    thumbnailReferences.current[currentIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [currentIndex, isOpen]);

  if (!images.length) {
    return null;
  }

  const goToPrevious = () =>
    setCurrentIndex(
      (previousIndex) => (previousIndex - 1 + images.length) % images.length,
    );

  const goToNext = () =>
    setCurrentIndex((previousIndex) => (previousIndex + 1) % images.length);

  return (
    <PageModal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="flex h-full w-full flex-col gap-32 pt-32">
        <div className="mx-auto flex w-[70%] flex-1 items-center gap-16">
          {images.length > 1 && (
            <IconButton
              variant="secondaryNeutral"
              iconName="arrow-left"
              aria-label="Previous photo"
              onClick={goToPrevious}
              className="shrink-0"
            />
          )}
          <div
            className="relative flex h-full min-h-0 w-full flex-1 items-center justify-center overflow-hidden"
            style={{
              backgroundImage: `url(${images[currentIndex]?.src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="bg-background-primary-default/80 text-content-primary text-caption absolute top-16 right-16 rounded-full px-12 py-4">
              {currentIndex + 1} / {images.length}
            </div>
          </div>
          {images.length > 1 && (
            <IconButton
              variant="secondaryNeutral"
              iconName="arrow-right"
              aria-label="Next photo"
              onClick={goToNext}
              className="shrink-0"
            />
          )}
        </div>
        <div className="mt-auto flex w-[100vw] flex-nowrap gap-8 overflow-x-auto overflow-y-hidden px-8 pt-4 pb-32">
          {images.map((image, index) => (
            <button
              type="button"
              key={image.src}
              ref={(element) => {
                thumbnailReferences.current[index] = element;
              }}
              className={classNames(
                'rounded-8 h-[120px] w-[120px] shrink-0 cursor-pointer overflow-hidden border-none hover:brightness-100',
                index === currentIndex
                  ? 'focus-ring outline outline-offset-[var(--grapes-focus-ring-offset)] outline-[var(--grapes-color-background-primary-brand-default)] brightness-100'
                  : 'brightness-92',
              )}
              onClick={() => setCurrentIndex(index)}
              aria-label={`View ${image.alt}`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </PageModal>
  );
};
